import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import AttachmentsSidebarSection from 'components/AttachmentsSidebarSection/AttachmentsSidebarSection';
import SidebarNavigation from 'components/SidebarNavigation';

import {
  selectActiveApplication,
  selectActiveUserId,
  selectApplications
} from 'features/applications/applicationsSelectors';
import { setActiveUserId } from 'features/sidebar/sidebarSlice';

import usePageviewEvent from 'hooks/usePageViewEvent';
import { useSyncApplicationRoute } from 'hooks/useSyncApplicationRoute';
import { useAppSelector } from 'hooks/useTypedRedux';

import { GTMEvents, JobseekerAttachmentTypes } from 'constants/enums';

interface ApplicationResumeViewProps {
  isLoading: boolean;
  fetchApplicationData: (applicationId: string) => Promise<void>;
}

const ApplicationResumeView: FC<ApplicationResumeViewProps> = ({
  isLoading,
  fetchApplicationData
}: ApplicationResumeViewProps) => {
  const { t } = useTranslation();

  const applicationId = useAppSelector(selectActiveUserId);
  const applications = useAppSelector(selectApplications);
  const application = useAppSelector(selectActiveApplication);

  useSyncApplicationRoute(applicationId);
  usePageviewEvent(GTMEvents.ApplicationsListViewSidebarResume);

  const attachments = application?.attachments || [];

  const handleAttachmentChange = () => {
    if (!applicationId) {
      return;
    }
    fetchApplicationData(applicationId);
  };

  const cvAttachments = attachments.filter(
    (el) => el.type === JobseekerAttachmentTypes.CV
  );

  return (
    <aside>
      {isLoading ? (
        <div>{t('common.loading')}</div>
      ) : (
        <>
          <SidebarNavigation
            id={applicationId}
            data={applications}
            action={setActiveUserId}
          />

          <AttachmentsSidebarSection
            name={t('sidebar.application.resume.title')}
            attachments={cvAttachments}
            attachmentType={JobseekerAttachmentTypes.CV}
            placeholder={t('sidebar.application.resume.placeholder')}
            onChange={handleAttachmentChange}
          />
        </>
      )}
    </aside>
  );
};

export default ApplicationResumeView;
